/**
 * The pharmacy portal (Treatment Plan spec v1.4, S-15 to S-19), at /pharmacy.
 *
 * Mounted outside the clinic's AuthProvider, like the patient portal: a
 * pharmacy user never passes through the clinic session, and its token lives
 * under its own key. Sign-in is a password and then an authenticator app code
 * (PH-003); the first sign-in sets the app up.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, EmptyState, ErrorState, Field, LoadingRows, inputClass } from '../../components/ui';
import { onPharmacySessionExpired, pharmacyApi, pharmacyToken, type PharmacyMe, type SignInStep } from '../../lib/pharmacy';
import { PharmacyClinicsPage, PharmacyContactsPage, PharmacySettingsPage, PharmacyUsersPage } from './PharmacyPages';
import { PriceSheetsPage, UploadPage } from './PharmacyPricing';

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.';
}

/* ------------------------------------------------------------------ *
 * Signing in
 * ------------------------------------------------------------------ */

function AuthFrame({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div className="min-h-screen bg-paper px-4 py-10 sm:py-16">
      <div className="mx-auto w-full max-w-[440px]">
        <div className="mb-6">
          <div className="font-display text-[30px] leading-none text-navy">MCOS</div>
          <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#4d6072]">Pharmacy portal</div>
        </div>
        <Card level="prominent">
          <h1 className="font-display text-[24px] text-navy">{title}</h1>
          {subtitle && <p className="mt-1 text-[14px] text-[#4d6072]">{subtitle}</p>}
          <div className="mt-5">{children}</div>
        </Card>
      </div>
    </div>
  );
}

/** "ABCD EFGH ..." so the key can be typed into an authenticator app without losing its place. */
const grouped = (secret: string) => secret.replace(/(.{4})/g, '$1 ').trim();

function CodeStep({ step, onDone }: { step: SignInStep; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await pharmacyApi.anonymous<{ accessToken: string }>('/auth/code', { mfaToken: step.mfaToken, code: code.trim() });
      pharmacyToken.set(result.accessToken);
      onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      {step.step === 'mfa_setup' && (
        <div className="rounded-lg border border-hairline bg-white p-4 text-[14px] text-navy">
          <p className="font-semibold">Set up your authenticator app</p>
          <p className="mt-1 text-[#4d6072]">Every pharmacy sign-in needs one. In Google Authenticator, 1Password or Authy, add an account by setup key and enter:</p>
          <p className="mt-3 break-all rounded-md bg-mint px-3 py-2 font-mono text-[15px] tracking-wider" data-testid="mfa-secret">{grouped(step.secret)}</p>
          <p className="mt-2 text-[12px] text-[#4d6072]">Account: MCOS pharmacy. Type: time based. On a phone with the app installed, <a className="text-forest underline" href={step.otpauthUrl}>open it directly</a>.</p>
        </div>
      )}
      <Field label="6-digit code from your authenticator app">
        <input className={inputClass} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus />
      </Field>
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      <Button type="submit" loading={busy} disabled={code.length !== 6} className="w-full">
        {step.step === 'mfa_setup' ? 'Turn on and sign in' : 'Sign in'}
      </Button>
    </form>
  );
}

function LoginPage({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<SignInStep | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setStep(await pharmacyApi.anonymous<SignInStep>('/auth/login', { email, password }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  if (step) return <AuthFrame title="Check it's you">{<CodeStep step={step} onDone={onSignedIn} />}</AuthFrame>;
  return (
    <AuthFrame title="Sign in" subtitle="For pharmacies that serve MCOS clinics.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email">
          <input className={inputClass} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password">
          <input className={inputClass} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <Button type="submit" loading={busy} disabled={!email || !password} className="w-full">Continue</Button>
      </form>
    </AuthFrame>
  );
}

function InvitePage({ onSignedIn }: { onSignedIn: () => void }) {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [step, setStep] = useState<SignInStep | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setStep(await pharmacyApi.anonymous<SignInStep>('/auth/accept-invite', { token, password }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  if (step) return <AuthFrame title="One more step">{<CodeStep step={step} onDone={onSignedIn} />}</AuthFrame>;
  return (
    <AuthFrame title="Set up your sign-in" subtitle="You were invited to the MCOS pharmacy portal.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Choose a password" hint="At least 12 characters.">
          <input className={inputClass} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Field label="Type it again" error={again && again !== password ? 'The two passwords are different.' : undefined}>
          <input className={inputClass} type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} />
        </Field>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <Button type="submit" loading={busy} disabled={!token || password.length < 12 || password !== again} className="w-full">Continue</Button>
      </form>
    </AuthFrame>
  );
}

function VerifyEmailPage() {
  const [params] = useSearchParams();
  const [state, setState] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    pharmacyApi
      .anonymous<{ email: string }>('/contacts/verify-email', { token: params.get('token') ?? '' })
      .then((r) => setState({ ok: true, text: `${r.email} is confirmed. MCOS will use it for the notices this contact handles.` }))
      .catch((err) => setState({ ok: false, text: errorText(err) }));
  }, [params]);
  return (
    <AuthFrame title="Confirm email">
      {state ? <p className={`text-[15px] ${state.ok ? 'text-forest' : 'text-red-600'}`}>{state.text}</p> : <LoadingRows rows={1} />}
    </AuthFrame>
  );
}

/* ------------------------------------------------------------------ *
 * The signed-in portal
 * ------------------------------------------------------------------ */

const NAV: Array<{ to: string; label: string; roles?: string[] }> = [
  { to: '/pharmacy', label: 'Home' },
  { to: '/pharmacy/inbox', label: 'Inbox' },
  { to: '/pharmacy/pricing', label: 'Price sheets', roles: ['admin', 'pricing'] },
  { to: '/pharmacy/clinics', label: 'Clinics' },
  { to: '/pharmacy/contacts', label: 'Points of contact' },
  { to: '/pharmacy/users', label: 'Users', roles: ['admin'] },
  { to: '/pharmacy/settings', label: 'Settings', roles: ['admin'] },
];

export function usePharmacyMe() {
  return useQuery({ queryKey: ['pharmacy-me'], queryFn: () => pharmacyApi.get<PharmacyMe>('/me') });
}

function HomePage() {
  const me = usePharmacyMe();
  if (me.error) return <ErrorState error={me.error} retry={() => void me.refetch()} />;
  if (!me.data) return <LoadingRows rows={5} />;
  const open = me.data.checklist.filter((c) => !c.done).length;
  return (
    <div className="space-y-5">
      <Card title="Before clinics can reach you" subtitle={open ? `${open} still to do. MCOS checks the license and records the BAA; the rest is yours.` : 'All set. Clinics can send you requests once their prescriptions go through DoseSpot.'}>
        <ul className="divide-y divide-hairline">
          {me.data.checklist.map((item) => (
            <li key={item.key} className="flex items-start gap-3 py-2.5 text-[14px]">
              <span aria-hidden="true" className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-bold ${item.done ? 'bg-forest text-white' : 'border border-[#c3d4cf] text-transparent'}`}>
                {item.done ? '✓' : ''}
              </span>
              <span className={item.done ? 'text-navy' : 'text-[#4d6072]'}>{item.label}</span>
            </li>
          ))}
        </ul>
      </Card>
      <div className="grid gap-5 sm:grid-cols-2">
        <Card title="Clinics">
          <p className="font-display text-[30px] text-navy">{me.data.clinics.linked}</p>
          <p className="text-[13px] text-[#4d6072]">linked{me.data.clinics.requests ? `, ${me.data.clinics.requests} asking to link` : ''}</p>
        </Card>
        <Card title="Reply times">
          <p className="text-[14px] text-navy">Reply within {me.data.pharmacy.replyTargetHours} hours</p>
          <p className="text-[13px] text-[#4d6072]">Backup contacts are alerted after {me.data.pharmacy.escalateAfterHours} hours</p>
        </Card>
      </div>
    </div>
  );
}

/** S-15. Requests start from a prescription sent through DoseSpot, so the inbox fills once DoseSpot is connected. */
function InboxPage() {
  const me = usePharmacyMe();
  const tabs = ['Needs reply', 'Answered', 'Resolved'];
  const [tab, setTab] = useState(tabs[0]);
  return (
    <Card padded={false}>
      <div className="flex gap-1 overflow-x-auto border-b border-hairline px-3 pt-3" role="tablist">
        {tabs.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
            className={`whitespace-nowrap rounded-t-md px-4 py-2 text-[14px] ${tab === t ? 'border-b-2 border-forest font-semibold text-navy' : 'text-[#4d6072] hover:text-navy'}`}>
            {t}
          </button>
        ))}
      </div>
      <EmptyState
        title="No requests yet"
        body={me.data?.pharmacy.baaSigned
          ? 'Requests from clinics about an order appear here: the clinic, the issue, the order and what to send back. They start once prescriptions go through DoseSpot.'
          : 'Requests from clinics about an order appear here once MCOS has recorded your BAA and prescriptions go through DoseSpot. Until then no patient information is sent to you.'}
      />
    </Card>
  );
}

function Shell({ onSignOut }: { onSignOut: () => void }) {
  const me = usePharmacyMe();
  const role = me.data?.user.role;
  const nav = NAV.filter((n) => !n.roles || (role && n.roles.includes(role)));
  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-hairline bg-[#f8f9f3]">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="flex items-baseline gap-3">
            <span className="font-display text-[26px] leading-none text-navy">MCOS</span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#4d6072]">Pharmacy portal</span>
          </div>
          <div className="flex items-center gap-4 text-[14px]">
            <div className="text-right">
              <div className="font-semibold text-navy">{me.data?.pharmacy.name ?? ''}</div>
              <div className="text-[12px] text-[#4d6072]">{me.data ? `${me.data.user.name}, ${me.data.user.role}` : ''}</div>
            </div>
            <Button variant="secondary" size="sm" onClick={onSignOut}>Sign out</Button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-[1200px] gap-1 overflow-x-auto px-3 sm:px-5" aria-label="Pharmacy portal">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === '/pharmacy'}
              className={({ isActive }) => `whitespace-nowrap border-b-2 px-3 py-2.5 text-[14px] ${isActive ? 'border-forest font-semibold text-navy' : 'border-transparent text-[#4d6072] hover:text-navy'}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-[1200px] px-4 py-6 sm:px-6">
        <Routes>
          <Route index element={<HomePage />} />
          <Route path="inbox" element={<InboxPage />} />
          <Route path="pricing" element={<PriceSheetsPage />} />
          <Route path="pricing/uploads/:id" element={<UploadPage />} />
          <Route path="clinics" element={<PharmacyClinicsPage />} />
          <Route path="contacts" element={<PharmacyContactsPage />} />
          <Route path="users" element={<PharmacyUsersPage />} />
          <Route path="settings" element={<PharmacySettingsPage />} />
          <Route path="*" element={<Navigate to="/pharmacy" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export function PharmacyApp() {
  const [signedIn, setSignedIn] = useState(() => pharmacyToken.get() != null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => onPharmacySessionExpired(() => setSignedIn(false)), []);
  const signedInNow = () => {
    void queryClient.removeQueries({ queryKey: ['pharmacy-me'] });
    setSignedIn(true);
    navigate('/pharmacy', { replace: true });
  };
  const signOut = () => {
    pharmacyToken.clear();
    queryClient.removeQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('pharmacy') });
    setSignedIn(false);
  };
  if (location.pathname.startsWith('/pharmacy/verify-email')) return <VerifyEmailPage />;
  if (location.pathname.startsWith('/pharmacy/invite')) return <InvitePage onSignedIn={signedInNow} />;
  if (!signedIn) return <LoginPage onSignedIn={signedInNow} />;
  return <Shell onSignOut={signOut} />;
}
